<script setup lang="ts">
import type {
  ElementInterface,
  GroupInterface,
  GroupSettingsInterface,
  SliceInterface,
} from '@voix/types'
import type { PropType, Ref, StyleValue } from 'vue'

import { useCookie } from '#imports'
import { useElementInView } from '@voix/composables/useElementInView'
import { useHighlightElement } from '@voix/composables/useHighlightElement'
import { vElementBounding } from '@vueuse/components'
import { useDebounceFn, useWindowSize } from '@vueuse/core'

import { computed, defineAsyncComponent, inject, onMounted, provide, ref, watch } from 'vue'
import { findBreakpointSettings } from '../../composables/useBreakpoints'

import {
  buildColorStyles,
  buildFlexStyles,
  buildGridStyles,
  buildMarginStyles,
  buildMaxWidthStyles,
  buildPaddingStyles,
} from '../../composables/useStyle'
import { usePageStore } from '../../store/pageStore'

const props = defineProps<{
  group: GroupInterface
}>()

const VoixGroupChild = defineAsyncComponent(
  // We have to type as Any due to the recursion and typescript
  () => import('@voix/components/page/VoixGroup.vue') as any,
)

const root = ref<HTMLElement>()
const groupRef = ref<any | null>(null)
provide('groupRef', groupRef)

const groupStyles = ref<{
  display?: string
  flexDirection?: string
  flexWrap?: string
  justifyContent?: string
  alignItems?: string
  columns?: string
  gridTemplateColumns?: string
  gap?: string
  columnGap?: string
  rowGap?: string
}>({})
const groupWrapperStyles = ref<{
  marginTop?: string
  marginRight?: string
  marginBottom?: string
  marginLeft?: string
  paddingTop?: string
  paddingRight?: string
  paddingBottom?: string
  paddingLeft?: string
  color?: string
  backgroundColor?: string
}>({})
const pageStore = usePageStore()

const groupId = computed(() => {
  return `voix-group-${props.group.id}`
})

const isContaining = computed(() => {
  return props.group.properties.contain
})

function generateGroupStyles() {
  const theGroup = Object.assign({}, props.group)

  if (!theGroup)
    return { properties: { settings: {} } }

  let styles: Record<string, string | number> = {}
  let wrapperStyles: Record<string, string | number> = {}

  if (theGroup.properties.settings) {
    const breakpointSettings = findBreakpointSettings(
      theGroup.properties.settings,
    ) as GroupSettingsInterface | null

    if (breakpointSettings) {
      if (breakpointSettings.position)
        styles.position = breakpointSettings.position

      if (breakpointSettings.displayMode)
        styles.display = breakpointSettings.displayMode.toString()

      styles = buildFlexStyles(styles, breakpointSettings)
      styles = buildGridStyles(styles, breakpointSettings)
      styles = buildMaxWidthStyles(styles, breakpointSettings)

      wrapperStyles = buildPaddingStyles(wrapperStyles, breakpointSettings)
      wrapperStyles = buildMarginStyles(wrapperStyles, breakpointSettings)
      wrapperStyles = buildColorStyles(wrapperStyles, breakpointSettings)
    }
  }

  groupWrapperStyles.value = wrapperStyles
  groupStyles.value = styles
}

generateGroupStyles()

// Debounced updates to styles
const updateGroupStyles = useDebounceFn(
  generateGroupStyles,
  10,
  { maxWait: 50 },
)

// Reset styles on window resize
const { width: windowWidth, height: windowHeight } = useWindowSize()
watch([windowWidth, windowHeight], () => {
  updateGroupStyles()
})

// Subscribe to changes in the store
// pageStore.$subscribe((mutation, state) => {
pageStore.$subscribe(() => {
  // TODO: This is a big one but this is firing on every slice in the page
  // To change this we'll need to fire a specific mutation on a specific slice
  // instead of updating the entire page. This will allow us to check the mutation events
  // and only update styles for the slice that needs it.

  // This will "speed up" and optimize the animations of changing background colors and such.
  updateGroupStyles()
})

watch(
  props.group,
  (value) => {
    if (value) {
      updateGroupStyles()
    }
  },
  { deep: true },
)

function setSliceElement(element: ElementInterface) {
  return element as SliceInterface
}

onMounted(() => {
  updateGroupStyles()
})

useElementInView(props.group, root)
const { highlightStyles, inStudio, onBounding } = useHighlightElement(props.group, groupId, groupWrapperStyles)

const boundingOptions = {
  reset: true,
  windowResize: true,
  windowScroll: true,
  immediate: false,
  updateTiming: 'sync',
}

// This figures out if the group is the topElement
const topElement = inject('topElement', null)
const isFirstElement = ref(false)
const parentIsFirstElement = inject('parentIsFirstElement', false)

if (topElement?.id === props.group.id) {
  isFirstElement.value = true
}
provide('parentIsFirstElement', isFirstElement.value || parentIsFirstElement)

// Showing container in editor
const showContainersInEditorCookie = useCookie('voix-show-containers-in-editor') as Ref<boolean>
const showContainersInEditor = computed(() => showContainersInEditorCookie.value === true)
</script>

<template>
  <div
    :id="groupId"
    ref="groupRef"
    v-element-bounding="[onBounding, boundingOptions]"
    class="voix-group-wrapper"
  >
    <div :class="{ 'container mx-auto relative': isContaining }">
      <div
        :id="group.properties.dom_id"
        ref="root"
        class="voix-group"
      >
        <template v-for="(element, key) in group.elements">
          <VoixSliceLoop
            v-if="element.type === 'slice'"
            :key="element.id"
            :element="setSliceElement(element)"
          />
          <VoixGroupChild
            v-if="element.type === 'group'"
            :key="key"
            :group="element"
          />
        </template>
      </div>

      <div
        v-if="inStudio && isContaining && showContainersInEditor"
        class="absolute z-30 inset-0 pointer-events-none flex justify-center"
      >
        <div class="bg-sky-500/50 w-full " />
      </div>
    </div>
  </div>
  <div
    v-if="inStudio"
    class="fixed z-[9999] box-border transition-opacity duration-200 pointer-events-none"
    :style="highlightStyles"
  />
</template>

<style scoped>
.voix-group-wrapper {
  margin-top: v-bind("groupWrapperStyles.marginTop");
  margin-right: v-bind("groupWrapperStyles.marginRight");
  margin-bottom: v-bind("groupWrapperStyles.marginBottom");
  margin-left: v-bind("groupWrapperStyles.marginLeft");
  padding-top: v-bind("groupWrapperStyles.paddingTop");
  padding-right: v-bind("groupWrapperStyles.paddingRight");
  padding-bottom: v-bind("groupWrapperStyles.paddingBottom");
  padding-left: v-bind("groupWrapperStyles.paddingLeft");
  color: v-bind("groupWrapperStyles.color");
  background-color: v-bind("groupWrapperStyles.backgroundColor");
}

.voix-group {
  display: v-bind("groupStyles.display");
  flex-direction: v-bind("groupStyles.flexDirection");
  flex-wrap: v-bind("groupStyles.flexWrap");
  justify-content: v-bind("groupStyles.justifyContent");
  align-items: v-bind("groupStyles.alignItems");
  columns: v-bind("groupStyles.columns");
  grid-template-columns: v-bind("groupStyles.gridTemplateColumns");
  column-gap: v-bind("groupStyles.gap || groupStyles.columnGap");
  row-gap: v-bind("groupStyles.gap || groupStyles.rowGap");
}
</style>
